import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { Plus, Minus } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

// FAQ items — sorted alphabetically by question (French locale).
// Kept in sync with FAQPAGE_JSONLD in /app/frontend/scripts/prerender.js
const faqs = [
  {
    q: "Combien de temps dure une mission avec FOD by DMB Partners ?",
    a: "En moyenne, lorsque nous avons toutes les informations nécessaires à la mission, nous obtenons les résultats escomptés en trois semaines.",
  },
  {
    q: "Combien puis-je économiser en moyenne ?",
    a: "Nos clients économisent 55 % en moyenne, jusqu'à 70 %, des frais bancaires qu'ils payaient jusque-là.",
  },
  {
    q: "Dois-je changer de banque ?",
    a: "Dans 90 % des cas, non. Notre objectif principal est d'optimiser les tarifs auprès de vos banques actuelles. Le changement d'établissement n'est envisagé que si vos partenaires historiques refusent d'aligner leurs conditions sur les prix pratiqués pour des entreprises comparables.",
  },
  {
    q: "En quoi êtes-vous différents d'un comparateur en ligne ou d'un cabinet généraliste ?",
    a: "Un comparateur en ligne donne des moyennes théoriques automatisées sans négocier pour vous. Un cabinet de conseil classique facture souvent des journées-hommes fixes (honoraires de mission) sans garantie de résultat. FOD by DMB Partners est un pure-player opérationnel : nous analysons l'ensemble de vos échelles d'intérêts et de vos tickets d'agios en détail, nous gérons la négociation de A à Z avec votre banque, et nous ne sommes payés que sur les sommes effectivement économisées.",
  },
  {
    q: "J'ai déjà renégocié il y a deux ans, ça vaut encore le coup ?",
    a: "Oui. Les grilles tarifaires bancaires évoluent chaque année et les marges appliquées par les banques ont fortement varié avec les mouvements de taux d'intérêt récents. De plus, la croissance de votre entreprise ou l'évolution de vos flux (volume de CB, devises) crée de nouveaux leviers de négociation qui n'existaient pas il y a 24 mois.",
  },
  {
    q: "J'ai également des lignes d'autorisation bancaires (facilité de caisse, lignes CAPEX, crédit-bail, financement moyen terme, etc.) ; puis-je bénéficier de vos services ?",
    a: "Oui, nous travaillons avec les équipes de DMB PARTNERS, cabinet one-stop-shop en stratégie financière et en levée de fonds, qui détient en totalité la marque FOD by DMB Partners. DMB PARTNERS peut vous accompagner. Vous n'avez qu'à faire la simulation de vos économies et lors du premier échange avec un membre de notre équipe, vous pourrez échanger sur votre situation financière et vos besoins. Nous pouvons par conséquent vous lever des fonds tout en réalisant la mission d'optimisation des frais bancaires.",
  },
  {
    q: "Ma banque va-t-elle mal le prendre ? Mes lignes de crédit sont-elles menacées ?",
    a: "Non. Notre démarche ne consiste pas à braquer votre banquier, mais à réaligner vos conditions sur la réalité du marché des PME/ETI. La négociation s'appuie sur des données objectives de marché (benchmarks). Vos chargés d'affaires ont l'habitude de ces ajustements et cela n'impacte en rien vos lignes de crédit, découvert ou encours de financement existants.",
  },
  {
    q: "Qu'est ce qu'une commission de mouvement ?",
    a: "C'est une prestation payante que votre banque vous facture pour l'utilisation de vos comptes bancaires. Chaque flux débiteur, hors les remboursements de vos prêts et des factures bancaires, génère alors une commission, qui peut aller jusqu'à 0,20%. Exemple : vous avez fait 1 000 000 euros de flux débiteurs en Janvier 2026, la banque va vous envoyer une facture de 2 000 euros en Février 2027.",
  },
  {
    q: "Qu'est-ce que l'optimisation des frais bancaires ?",
    a: "C'est une méthodologie que FOD by DMB Partners a développé et qui consiste à l'audit de vos frais bancaires d'utilisation de compte, à la négociation auprès de vos partenaires actuels bancaires ou bien des nouveaux selon vos besoins tout en ayant des conseils sur le pilotage sur vos flux bancaires dans le but final de vous permettre de réaliser des économies substantielles.",
  },
  {
    q: "Que se passe-t-il si mon banquier refuse de renégocier ?",
    a: "C'est extrêmement rare lorsqu'un dossier est étayé par nos chiffres de marché. Si un refus survient, nous vous présentons les alternatives du marché ou des leviers de mise en concurrence (multi-bancarisation) sans vous contraindre. Et si aucune économie n'est appliquée dans les faits, vous ne nous devez rien.",
  },
  {
    q: "Quelles sont les conditions pour être accompagnées par FOD by DMB Partners ?",
    a: "Pour le moment, nous travaillons exclusivement avec les entreprises réalisant plus de 2 millions d'euros de chiffre d'affaires annuel sur la quasi totalité des secteurs. Si vous faites moins que 2 millions d'euros de chiffre d'affaires mais que vous avez un business plan qui vous permet de le dépasser rapidement, vous pouvez directement nous écrire sur l'onglet Contact en expliquant votre situation. Nous vous recontacterons très rapidement.",
  },
  {
    q: "Touchez-vous une commission des banques ?",
    a: "Non, aucune. FOD by DMB Partners est un pure player totalement indépendant des établissements financiers. Nous ne percevons aucun rétro-commissionnement, courtage ou apport d'affaires de la part des banques. Notre alignement d'intérêts est exclusivement tourné vers vous : plus vos frais baissent, plus notre mission est réussie.",
  },
];

export default function FAQ() {
  useDocumentMeta({
    title: "FAQ — Vos questions sur l'optimisation bancaire — FOD by DMB Partners",
    description: "Réponses aux questions fréquentes sur l'audit et l'optimisation des frais bancaires PME / ETI par FOD by DMB Partners.",
    canonical: "https://www.fodbydmb.com/faq",
  });

  // FAQPage schema.org — injected in <head> so search engines (Google, Bing)
  // see it in the same location whether the page is landed directly (prerender
  // static injection) or reached via client-side SPA routing.
  // De-duplicates the prerender's `data-testid="faq-jsonld-static"` if present.
  useEffect(() => {
    const faqJsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    };
    const existing = document.querySelector(
      'script[data-testid="faq-jsonld"], script[data-testid="faq-jsonld-static"]'
    );
    const script = existing || document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-testid", "faq-jsonld");
    script.textContent = JSON.stringify(faqJsonLd);
    if (!existing) document.head.appendChild(script);
    return () => {
      // Only remove if we appended (i.e., no prerender static block was already there)
      if (!existing && script.parentNode) script.parentNode.removeChild(script);
    };
  }, []);

  return (
    <div data-testid="faq-page">
      <div className="mx-auto max-w-[1180px] px-4 md:px-8 lg:px-12 pt-10 md:pt-16 lg:pt-20 pb-12 md:pb-16 lg:pb-24 flex flex-wrap gap-x-16 gap-y-10 items-start">
        <FaqIntro />
        <AccordionPrimitive.Root
          type="single"
          collapsible
          defaultValue="item-0"
          className="flex-[999_1_560px] min-w-0 flex flex-col bg-white border border-border-default rounded-2xl overflow-hidden"
        >
          {faqs.map((f, i) => (
            <FaqItem key={f.q} f={f} i={i} />
          ))}
        </AccordionPrimitive.Root>
      </div>
    </div>
  );
}

const FaqIntro = () => (
  <div className="flex-1 basis-[300px] min-w-0 flex flex-col gap-[18px] lg:sticky lg:top-6 animate-fade-up">
    <div className="text-[13px] font-semibold uppercase tracking-[0.16em] text-gold-text">FAQ</div>
    <h1 className="m-0 font-bold text-[32px] md:text-5xl lg:text-[56px] leading-[1.12] tracking-[-0.025em] text-navy">
      Une question ? C'est par ici.
    </h1>
    <div className="w-[140px] h-1.5 bg-gold" />
    <div className="bg-navy text-cream rounded-[14px] p-6 flex flex-col gap-3 mt-3">
      <span className="text-lg font-bold">Vous ne trouvez pas votre réponse ?</span>
      <span className="text-sm leading-relaxed text-on-navy-soft">Écrivez-nous, un ancien banquier vous répond.</span>
      <Link
        to="/contact"
        data-testid="faq-contact-cta"
        className="h-12 flex items-center justify-center rounded-md bg-gold text-navy text-[15px] font-semibold transition-[transform,box-shadow] hover:-translate-y-[1px] hover:shadow-lg"
      >
        Nous contacter
      </Link>
    </div>
  </div>
);

const FaqItem = ({ f, i }) => (
  <AccordionPrimitive.Item value={`item-${i}`} data-testid={`faq-${i}`} className="group border-b border-border-soft last:border-b-0">
    <AccordionPrimitive.Header asChild>
      <h2 className="m-0 text-base">
        <AccordionPrimitive.Trigger
          data-testid={`faq-trigger-${i}`}
          className="w-full min-h-[64px] px-[18px] md:px-7 py-[18px] flex items-center justify-between gap-4 text-left text-base font-semibold leading-[1.45] text-navy transition-colors hover:text-gold-text"
        >
          <span>{f.q}</span>
          <Plus size={20} className="shrink-0 group-data-[state=open]:hidden" aria-hidden="true" />
          <Minus size={20} className="shrink-0 text-gold-text hidden group-data-[state=open]:block" aria-hidden="true" />
        </AccordionPrimitive.Trigger>
      </h2>
    </AccordionPrimitive.Header>
    <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
      <p className="m-0 px-[18px] md:px-7 pb-[22px] text-[15px] leading-[1.75] text-grey-text">{f.a}</p>
    </AccordionPrimitive.Content>
  </AccordionPrimitive.Item>
);
